/**
 * Week Context Utility
 * Canonical week identity generator with timezone support
 * SERVER ONLY - Source of truth for week boundaries
 */

export interface WeekContext {
  weekId: string; // Format: "2025-41-America/Barbados"
  startDate: Date;
  endDate: Date;
  timezone: string;
}

/**
 * Generate canonical week context (SERVER ONLY)
 * Uses user's timezone (default: America/Barbados, no DST)
 *
 * @param date - Date to calculate week for
 * @param timezone - IANA timezone identifier
 * @param weekStart - Day of week start (0=Sunday, 1=Monday)
 */
export function getWeekContext(
  date: Date,
  timezone: string = 'America/Barbados',
  weekStart: number = 0, // Default: Sunday
): WeekContext {
  // Get ISO week number
  const tempDate = new Date(date);
  tempDate.setHours(0, 0, 0, 0);
  tempDate.setDate(tempDate.getDate() + 4 - (tempDate.getDay() || 7));
  const yearStart = new Date(tempDate.getFullYear(), 0, 1);
  const weekNo = Math.ceil((((tempDate.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);

  // Calculate week boundaries
  const startDate = new Date(tempDate);
  startDate.setDate(tempDate.getDate() - tempDate.getDay() + weekStart);
  startDate.setHours(0, 0, 0, 0);

  const endDate = new Date(startDate);
  endDate.setDate(startDate.getDate() + 6);
  endDate.setHours(23, 59, 59, 999);

  return {
    weekId: `${tempDate.getFullYear()}-${String(weekNo).padStart(2, '0')}-${timezone}`,
    startDate,
    endDate,
    timezone,
  };
}

/**
 * Parse weekId back to context components
 * @param weekId - Format: "2025-41-America/Barbados"
 * @returns Parsed year, week, and timezone
 */
export function parseWeekId(weekId: string): {
  year: number;
  week: number;
  timezone: string;
} {
  const match = weekId.match(/^(\d{4})-(\d{2})-([A-Za-z_\/-]+)$/);
  if (!match) {
    throw new Error(`Invalid weekId format: ${weekId}. Expected format: YYYY-WW-Timezone`);
  }

  return {
    year: parseInt(match[1], 10),
    week: parseInt(match[2], 10),
    timezone: match[3],
  };
}

/**
 * Get current week context for a user
 * @param timezone - User's timezone
 * @param weekStart - User's week start preference
 */
export function getCurrentWeekContext(
  timezone: string = 'America/Barbados',
  weekStart: number = 0,
): WeekContext {
  return getWeekContext(new Date(), timezone, weekStart);
}
